import React from "react";
import ecosystemImage from "../assets/integrated-healthcare-ecosystem.jpg";

const PatientCare = () => (
  <>
    <style>{`
      @keyframes slideInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes slideInLeft {
        from {
          opacity: 0;
          transform: translateX(-50px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }

      @keyframes bounce {
        0%, 100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-10px);
        }
      }

      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.7;
        }
      }

      .patient-header {
        animation: slideInUp 0.8s ease-out;
      }

      .patient-content {
        animation: slideInLeft 0.8s ease-out 0.2s both;
      }

      .care-feature {
        animation: slideInUp 0.6s ease-out;
        transition: all 0.4s ease;
      }

      .care-feature:nth-child(1) { animation-delay: 0.2s; }
      .care-feature:nth-child(2) { animation-delay: 0.3s; }
      .care-feature:nth-child(3) { animation-delay: 0.4s; }

      .care-feature:hover {
        transform: translateY(-10px);
        box-shadow: 0 20px 40px rgba(59, 130, 246, 0.15);
      }

      .care-icon {
        animation: bounce 2s ease-in-out infinite;
      }

      .care-feature:nth-child(1) .care-icon { animation-delay: 0s; }
      .care-feature:nth-child(2) .care-icon { animation-delay: 0.2s; }
      .care-feature:nth-child(3) .care-icon { animation-delay: 0.4s; }
    `}</style>

    <section id="patient-care" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16 patient-header">
          <span className="inline-flex items-center gap-2 bg-slate-100 border border-slate-300 rounded-full px-4 py-2 text-xs font-semibold text-slate-700 mb-6">
            <span className="w-2 h-2 bg-slate-700 rounded-full" />
            Patient Care Integration
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Unified Digital Health Records & Care Coordination
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
          <div className="patient-content">
            <div className="space-y-6">
              <div className="care-feature bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                <div className="flex items-start gap-4">
                  <div className="text-4xl care-icon">📋</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Centralized Health Records</h3>
                    <p className="text-gray-600">All health records stored under your unique patient ID for seamless access across the network.</p>
                  </div>
                </div>
              </div>

              <div className="care-feature bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 border border-teal-200">
                <div className="flex items-start gap-4">
                  <div className="text-4xl care-icon">💊</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Pharmacy Integration</h3>
                    <p className="text-gray-600">Prescriptions automatically sent to your nearest pharmacy for instant preparation and delivery.</p>
                  </div>
                </div>
              </div>

              <div className="care-feature bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6 border border-emerald-200">
                <div className="flex items-start gap-4">
                  <div className="text-4xl care-icon">🔬</div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Digital Lab Reports</h3>
                    <p className="text-gray-600">Lab results uploaded directly to your profile for immediate access and doctor consultation.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <img 
              src={ecosystemImage} 
              alt="Integrated Healthcare Ecosystem" 
              className="w-full rounded-2xl shadow-xl object-cover"
            />
          </div>
        </div>

        {/* Data Flow */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-8">Your Digital Health Journey</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: "1", title: "Consultation", icon: "👨‍⚕️" },
              { step: "2", title: "Prescription", icon: "📄" },
              { step: "3", title: "Medication", icon: "💊" },
              { step: "4", title: "Follow-up", icon: "✅" }
            ].map((item) => (
              <div key={item.step} className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30 text-center">
                <div className="text-4xl mb-2">{item.icon}</div>
                <div className="font-bold">{item.title}</div>
                <div className="text-sm text-white/80 mt-1">Step {item.step}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  </>
);

export default PatientCare;
